import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

const TRANSITION_DURATION = 500; // 0.3초

// [스타일 객체]
const styles = {
    // --- BottomSheet 기본 ---
    sheetOverlay: {
        position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1100,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        opacity: 0, transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`,
        pointerEvents: 'none',
    },
    sheetOverlayActive: { opacity: 1, pointerEvents: 'auto' },
    sheetContent: {
        width: '100%', height: 'auto', maxHeight: '90vh', backgroundColor: '#202126', borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px', padding: '24px 24px calc(env(safe-area-inset-bottom, 34px) + 24px)', boxSizing: 'border-box', color: '#FCFCFA',
        boxShadow: '0px -4px 6px 0px #00000033', transform: 'translateY(100%)',
        transition: `transform ${TRANSITION_DURATION}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
        display: 'flex', flexDirection: 'column',
    },
    sheetContentActive: { transform: 'translateY(0)' },

    // --- 헤더 ---
    sheetHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '24px', flexShrink: 0,
    },
    sheetTitle: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '20px',
        color: '#FCFCFA', margin: 0,
    },
    sheetCloseButton: {
        background: 'transparent', border: 'none', cursor: 'pointer',
        padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },

    // --- 날짜 선택 섹션 ---
    bsDateSection: { width: '100%', marginBottom: '32px', flexShrink: 0 },
    bsSectionTitle: {
        fontFamily: 'Pretendard', fontWeight: 600, fontSize: '18px',
        color: '#FCFCFA', margin: '0 0 12px 0', textAlign: 'left',
    },
    dateSelectDiv: {
        width: '100%', display: 'flex', overflowX: 'auto', gap: '8px',
        paddingBottom: '4px',
    },
    dateSelectButtonInactive: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        width: '74px', height: '72px', flexShrink: 0, borderRadius: '8px',
        padding: '10px 0', gap: '4px', cursor: 'pointer',
        background: '#2C2C35', border: '1px solid #3A394A',
    },
    dateSelectButtonActive: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        width: '74px', height: '72px', flexShrink: 0, borderRadius: '8px',
        padding: '10px 0', gap: '4px', cursor: 'pointer',
        background: '#F5F8F9', border: 'none',
    },
    dateNumberInactive: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#F5F8F9' },
    dateNumberActive: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#8052FF' },
    dateTextInactive: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#9A9A9A' },
    dateTextActive: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#8052FF' },

    // --- 시간 선택 섹션 ---
    bsTimeSection: {
        width: '100%', display: 'flex', flexDirection: 'column', gap: '16px',
        marginBottom: '32px', flexShrink: 0,
    },
    bsTimeAmPmSelector: { display: 'flex', gap: '8px', width: '100%' },
    bsAmPmButtonBase: {
        flex: 1, height: '40px', borderRadius: '8px', border: '1px solid',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', backgroundColor: 'transparent',
    },
    bsAmPmButtonActive: { borderColor: '#8052FF', color: '#8052FF' },
    bsAmPmButtonInactive: { borderColor: '#3A394A', color: '#747474' },
    bsAmPmText: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '14px' },
    bsTimeGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', width: '100%',
        maxHeight: '200px', overflowY: 'auto',
    },
    bsTimeSlotBase: {
        height: '40px', borderRadius: '8px', display: 'flex',
        alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
        border: '1px solid', backgroundColor: 'transparent',
    },
    bsTimeSlotActive: { borderColor: '#8052FF', backgroundColor: '#8052FF33' },
    bsTimeSlotInactive: { borderColor: '#3A394A' },
    bsTimeSlotClosed: { borderColor: '#2C2C35', backgroundColor: '#2C2C35', cursor: 'not-allowed' },
    bsTimeTextActive: { fontFamily: 'Pretendard', fontWeight: 600, fontSize: '14px', color: '#8052FF' },
    bsTimeTextInactive: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#FCFCFA' },
    bsTimeTextClosed: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#555555', textDecoration: 'line-through'
    },
    bsTimeSelectPlaceholder: {
        gridColumn: '1 / -1', textAlign: 'center', padding: '20px 0',
        color: '#747474', fontSize: '14px', fontFamily: 'Pretendard',
    },
    sheetButtonField: { display: 'flex', gap: '8px', width: '100%', marginTop: 'auto' },
    sheetCancelButton: {
        flex: 1, height: '56px', borderRadius: '12px', border: 'none',
        backgroundColor: '#3A394A', color: '#FFFFFF', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
    },
    sheetConfirmButton: {
        flex: 2, height: '56px', borderRadius: '12px', border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: '#FFFFFF', fontSize: '16px', fontWeight: 700, cursor: 'pointer',
    },
};

// [컴포넌트] 스크롤바 숨김 스타일
const HideScrollbarStyles = () => (
    <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
    `}</style>
);


/**
 * [핵심 수정] 데이터 파싱 함수
 * - 날짜 비교 로직 삭제 (API가 준 데이터 무조건 신뢰)
 * - 문자열 데이터 처리 유지
 */
/**
 * API 시간 데이터를 UI용 데이터로 변환 및 분류하는 함수
 */
const processApiTimes = (times, targetDate) => {
    const am = [];
    const pm = [];

    if (!Array.isArray(times)) return { am, pm };

    // 현재 시간 구하기
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // 전체를 묶는 isToday 검사 제거 (아이템별로 평가)

    times.forEach((item, index) => {
        // 객체 데이터 처리
        if (typeof item === 'object' && item !== null) {
            let timeStr = String(item.stStime || item.time || "").trim();
            if (!timeStr) return;

            let hour = 0;
            let minute = "";

            // 시간 파싱
            if (timeStr.includes(':')) {
                const parts = timeStr.split(':');
                hour = parseInt(parts[0], 10);
                minute = parts[1];
            } else if (timeStr.length === 4 && !isNaN(timeStr)) {
                hour = parseInt(timeStr.substring(0, 2), 10);
                minute = timeStr.substring(2);
            } else {
                return;
            }

            // ▼▼▼ [핵심 수정] 지난 시간 체크 로직 ▼▼▼
            // 아이템 객체 내에 날짜 데이터(stdate 등)가 있다면 최우선 적용, 없으면 외부에서 받은 targetDate 사용
            const itemDateStr = item.stdate || item.date || targetDate;
            const isItemToday = itemDateStr === todayStr;

            let isTimePassed = false;
            if (isItemToday) {
                const itemHour = hour;
                const itemMinute = parseInt(minute, 10);

                // 시간이 지났거나, 같은 시간인데 분이 지났으면 true
                if (itemHour < currentHour || (itemHour === currentHour && itemMinute < currentMinute)) {
                    isTimePassed = true;
                }
            }
            // ▲▲▲ ---------------------------------- ▲▲▲

            let displayHour = hour;
            if (hour > 12) displayHour = hour - 12;
            if (displayHour === 0) displayHour = 12;

            const label = `${displayHour}:${minute}`;
            const period = hour >= 12 ? '오후' : '오전';

            // 기존 마감 여부(cnt 등)와 시간 경과 여부를 합쳐서 결정
            // const isClosedByCnt = (item.cnt !== undefined && Number(item.cnt) <= 0);
            const isClosed = isTimePassed; // || isClosedByCnt;

            const slot = { id: index, time: label, original: timeStr, isClosed: isClosed, stKey: item.stKey };

            if (period === '오전') am.push(slot);
            else pm.push(slot);
        }
        // 문자열 데이터 처리
        else if (typeof item === 'string' || typeof item === 'number') {
            let timeStr = String(item).trim();
            let hour = 0;
            let minute = "";

            if (timeStr.length === 4 && !isNaN(timeStr)) {
                hour = parseInt(timeStr.substring(0, 2), 10);
                minute = timeStr.substring(2);
            } else if (timeStr.includes(':')) {
                const parts = timeStr.split(':');
                hour = parseInt(parts[0], 10);
                minute = parts[1];
            } else {
                return;
            }

            let displayHour = hour;
            if (hour > 12) displayHour = hour - 12;
            if (displayHour === 0) displayHour = 12;

            const label = `${displayHour}:${minute}`;
            const period = hour >= 12 ? '오후' : '오전';
            const slot = { id: index, time: label, original: timeStr, isClosed: false };

            if (period === '오전') am.push(slot);
            else pm.push(slot);
        }
    });

    // 시간순 정렬
    const sortFunc = (a, b) => parseInt(a.original.replace(':', '')) - parseInt(b.original.replace(':', ''));
    am.sort(sortFunc);
    pm.sort(sortFunc);

    return { am, pm };
};

function BottomSheet({ show, onClose, onConfirm, initialData, availableTimes = [], onDateChange, selectedDate, brName }) {
    const [isVisible, setIsVisible] = useState(show);

    const [dates, setDates] = useState([]);
    const [selectedDateId, setSelectedDateId] = useState(null);
    const [selectedAmPm, setSelectedAmPm] = useState('오후');
    const [selectedTime, setSelectedTime] = useState(null);

    // targetDateString 구하기 (메모이제이션)
    const targetDateString = useMemo(() => {
        const selected = dates.find(d => d.id === selectedDateId);
        return selected ? selected.apiDate : null;
    }, [dates, selectedDateId]);

    // processApiTimes에 기준 날짜 전달 (내부에서 즉각 업데이트된 targetDateString을 우선 신뢰)
    const { am: amTimes, pm: pmTimes } = useMemo(() => {
        const dateToUse = targetDateString || selectedDate;
        return processApiTimes(availableTimes, dateToUse);
    }, [availableTimes, targetDateString, selectedDate]);


    // 날짜 생성 (30일치 보장)
    useEffect(() => {
        const getDates = () => {
            const days = [];
            const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
            let currentDate = new Date();
            for (let i = 0; i < 30; i++) {
                const dayOfWeek = currentDate.getDay();
                const id = `date-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
                const dateText = `${currentDate.getMonth() + 1}.${currentDate.getDate()}`;
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const apiDate = `${year}-${month}-${day}`;
                days.push({
                    id: id,
                    dayNumber: currentDate.getDate(),
                    dayName: dayNames[dayOfWeek],
                    fullDate: dateText,
                    apiDate: apiDate
                });
                currentDate.setDate(currentDate.getDate() + 1);
            }
            return days;
        };
        setDates(getDates());
    }, []);

    // 부모 컴포넌트의 selectedDate가 변경되면 바텀시트 탭 UI(selectedDateId) 동기화
    useEffect(() => {
        if (selectedDate && dates.length > 0) {
            const matchedDate = dates.find(d => d.apiDate === selectedDate);
            if (matchedDate && matchedDate.id !== selectedDateId) {
                setSelectedDateId(matchedDate.id);
            }
        }
    }, [selectedDate, dates, selectedDateId]);

    // 초기 데이터 동기화 & 날짜 자동 선택 시 API 호출
    useEffect(() => {
        if (show) {
            setIsVisible(true);
            if (dates.length > 0) {
                if (!selectedDateId) {
                    const matchedDate = selectedDate ? dates.find(d => d.apiDate === selectedDate) : null;
                    const initialDate = matchedDate || dates[0]; // 전달받은 날짜가 우선, 없으면 오늘
                    setSelectedDateId(initialDate.id);
                    
                    // 부모가 전달한 날짜가 없어서 오늘로 강제 세팅한 경우에만 onDateChange 호출하여 데이터 요청
                    if (onDateChange && !matchedDate) onDateChange(initialDate.apiDate);
                }
            }
            if (initialData && initialData.time) {
                const parts = initialData.time.split(' ');
                if (parts.length === 2) {
                    setSelectedAmPm(parts[0]);
                    setSelectedTime(parts[1]);
                }
            } else {
                // 데이터 있는 쪽 우선 선택
                if (pmTimes.length > 0 && amTimes.length === 0) setSelectedAmPm('오후');
                else if (amTimes.length > 0 && pmTimes.length === 0) setSelectedAmPm('오전');
                else setSelectedAmPm('오후'); // 둘 다 있거나 없으면 오후 기본

                setSelectedTime(null);
            }
        } else {
            const timer = setTimeout(() => setIsVisible(false), TRANSITION_DURATION);
            return () => clearTimeout(timer);
        }
    }, [show, dates, pmTimes.length, amTimes.length]); // 의존성 추가

    const handleDateClick = (dateObj) => {
        setSelectedDateId(dateObj.id);
        setSelectedTime(null);
        if (onDateChange) onDateChange(dateObj.apiDate);
    };

    const handleConfirmClick = () => {
        if (!selectedDateId || !selectedTime) {
            alert("날짜와 시간을 모두 선택해주세요.");
            return;
        }
        const selectedDateObj = dates.find(d => d.id === selectedDateId);
        const dateStr = selectedDateObj ? `${selectedDateObj.fullDate} (${selectedDateObj.dayName})` : '';
        const currentList = selectedAmPm === '오전' ? amTimes : pmTimes;
        const selectedSlot = currentList.find(slot => slot.time === selectedTime);
        const stKey = selectedSlot ? selectedSlot.stKey : null;
        const reservationData = {
            date: dateStr,
            time: `${selectedAmPm} ${selectedTime}`,
            coachName: initialData?.coachName,
            stKey: stKey
        };

        try {
            const existing = JSON.parse(sessionStorage.getItem('myReservations') || '[]');
            existing.push(reservationData);
            sessionStorage.setItem('myReservations', JSON.stringify(existing));
        } catch (e) {
            console.error(e);
        }

        if (onConfirm) onConfirm(reservationData);
        onClose();
    };

    const currentTimes = selectedAmPm === '오전' ? amTimes : pmTimes;

    if (!isVisible) return null;

    return createPortal(
        <>
            <HideScrollbarStyles />
            <div style={{ ...styles.sheetOverlay, ...(show ? styles.sheetOverlayActive : {}) }} onClick={onClose}>
                <div style={{ ...styles.sheetContent, ...(show ? styles.sheetContentActive : {}) }}
                    onClick={(e) => e.stopPropagation()}>

                    <div style={styles.sheetHeader}>
                        <h2 style={styles.sheetTitle}>
                            {initialData?.coachName ? `${initialData.coachName} 예약` : '예약 정보 입력'}
                        </h2>
                        <button style={styles.sheetCloseButton} onClick={onClose}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18" stroke="#FCFCFA" strokeWidth="2" strokeLinecap="round"
                                    strokeLinejoin="round" />
                                <path d="M6 6L18 18" stroke="#FCFCFA" strokeWidth="2" strokeLinecap="round"
                                    strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    {/* 홈 예약 바텀 시트 날짜 선택 영역 */}
                    {/* <div style={styles.bsDateSection}>
                        <p style={styles.bsSectionTitle}>날짜 선택</p>
                        <div className="hide-scrollbar" style={styles.dateSelectDiv}>
                            {dates.map((date) => {
                                const isSelected = date.id === selectedDateId;
                                return (
                                    <div key={date.id}
                                        style={isSelected ? styles.dateSelectButtonActive : styles.dateSelectButtonInactive}
                                        onClick={() => handleDateClick(date)}>
                                        <span
                                            style={isSelected ? styles.dateNumberActive : styles.dateNumberInactive}>{date.dayNumber}</span>
                                        <span
                                            style={isSelected ? styles.dateTextActive : styles.dateTextInactive}>{date.dayName}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div> */}

                    {/* 홈 예약 바텀 시트 시간 선택 영역 */}
                    <div style={styles.bsTimeSection}>
                        {(() => {
                            const selectedDateObj = dates.find(d => d.id === selectedDateId);
                            const displayDateStr = selectedDateObj ? `${parseInt(selectedDateObj.apiDate.split('-')[1])}월 ${selectedDateObj.dayNumber}일 (${selectedDateObj.dayName})` : '';
                            
                            const branchName = brName || initialData?.brName || initialData?.centerName || '';
                            const finalText = branchName ? `[${branchName}] ${displayDateStr}` : displayDateStr;

                            return displayDateStr && (
                                <p style={{ ...styles.bsSectionTitle, color: '#8052FF', fontSize: '16px', marginBottom: '8px' }}>
                                    {finalText}
                                </p>
                            );
                        })()}
                        <p style={styles.bsSectionTitle}>시간 선택</p>
                        <div style={styles.bsTimeAmPmSelector}>
                            {['오전', '오후'].map((ampm) => (
                                <button key={ampm}
                                    style={{ ...styles.bsAmPmButtonBase, ...(selectedAmPm === ampm ? styles.bsAmPmButtonActive : styles.bsAmPmButtonInactive) }}
                                    onClick={() => {
                                        setSelectedAmPm(ampm);
                                        setSelectedTime(null);
                                    }}>
                                    <span style={styles.bsAmPmText}>{ampm}</span>
                                </button>
                            ))}
                        </div>
                        <div style={styles.bsDivider} />
                        <div className="hide-scrollbar" style={styles.bsTimeGrid}>
                            {currentTimes.length === 0 && (
                                <p style={styles.bsTimeSelectPlaceholder}>선택 가능한 시간이 없습니다.</p>)}
                            {currentTimes.map((timeSlot, index) => {
                                const isSelected = selectedTime === timeSlot.time;
                                const isClosed = timeSlot.isClosed;
                                let slotStyle = styles.bsTimeSlotBase;
                                if (isClosed) slotStyle = { ...slotStyle, ...styles.bsTimeSlotClosed };
                                else if (isSelected) slotStyle = { ...slotStyle, ...styles.bsTimeSlotActive };
                                else slotStyle = { ...slotStyle, ...styles.bsTimeSlotInactive };
                                return (
                                    <div key={index} style={slotStyle} onClick={() => {
                                        if (!isClosed) setSelectedTime(timeSlot.time);
                                    }}>
                                        <span
                                            style={isClosed ? styles.bsTimeTextClosed : (isSelected ? styles.bsTimeTextActive : styles.bsTimeTextInactive)}>{timeSlot.time}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div style={styles.sheetButtonField}>
                        <button style={styles.sheetCancelButton} onClick={onClose}> 취소</button>
                        <button style={styles.sheetConfirmButton} onClick={handleConfirmClick}> 확인</button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

export default BottomSheet;